import React from 'react';
import { DocPageLayout } from '../../components/DocPageLayout';
import { StepList } from '../../components/StepList';
import { CodeBlock } from '../../components/CodeBlock';
interface CheckoutIntegrationStepsPageProps {
  onNavigate?: (pageId: string) => void;
}
export function CheckoutIntegrationStepsPage({
  onNavigate
}: CheckoutIntegrationStepsPageProps) {
  const steps = [{
    title: 'Create a Checkout Session',
    content: <>
          <p data-id="element-1284">
            Send a{' '}
            <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono" data-id="element-1285">
              CREATE_CHECKOUT_SESSION
            </code>{' '}
            request from your server to the gateway API. This initializes the
            payment and returns a session ID and a{' '}
            <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono" data-id="element-1286">
              successIndicator
            </code>
            .
          </p>
          <CodeBlock language="cURL" title="Create Session Request" code={`curl https://test-gateway.mastercard.com/api/rest/version/72/merchant/{merchantId}/session \\
  -u "merchant.{merchantId}:{apiPassword}" \\
  -X POST \\
  -H "Content-Type: application/json" \\
  -d '{
    "apiOperation": "CREATE_CHECKOUT_SESSION",
    "order": {
      "id": "ORDER-12345",
      "amount": "100.00",
      "currency": "USD"
    },
    "interaction": {
      "operation": "PURCHASE",
      "returnUrl": "https://your-site.com/receipt",
      "cancelUrl": "https://your-site.com/cancel"
    }
  }'`} data-id="element-1287" />
        </>
  }, {
    title: 'Include the Checkout.js Library',
    content: <>
          <p data-id="element-1288">
            Add the Checkout JavaScript library to your payment page. The
            library is hosted by the gateway and provides the payment UI.
          </p>
          <CodeBlock language="HTML" title="Script Include" code={`<script src="https://test-gateway.mastercard.com/checkout/version/72/checkout.js"
        data-error="errorCallback"
        data-cancel="cancelCallback">
</script>`} data-id="element-1289" />
        </>
  }, {
    title: 'Configure and Display the Payment Page',
    content: <>
          <p data-id="element-1290">
            Configure the checkout with the session ID from Step 1 and display
            the payment page. You can use either a lightbox overlay or a
            full-page redirect.
          </p>
          <CodeBlock language="JavaScript" title="Configure Checkout" code={`Checkout.configure({
  session: {
    id: "SESSION000212345678901234567890"
  }
});

// Option 1: Show as lightbox overlay
Checkout.showLightbox();

// Option 2: Show as full payment page
Checkout.showPaymentPage();`} data-id="element-1291" />
        </>
  }, {
    title: 'Handle the Response',
    content: <p data-id="element-1292">
          After the payer completes or cancels the payment, they are redirected
          to your{' '}
          <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono" data-id="element-1293">
            returnUrl
          </code>{' '}
          or{' '}
          <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono" data-id="element-1294">
            cancelUrl
          </code>
          . The gateway appends a{' '}
          <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono" data-id="element-1295">
            resultIndicator
          </code>{' '}
          parameter. Compare this with the{' '}
          <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono" data-id="element-1296">
            successIndicator
          </code>{' '}
          from Step 1 to determine if the payment was successful. Always verify
          the result with a server-side API call.
        </p>
  }];
  return <DocPageLayout title="Integration Steps" data-id="element-1297">
      <div className="text-sm text-gray-500 mb-6 -mt-8" data-id="element-1298">
        <button onClick={() => onNavigate?.('overview')} className="hover:underline text-orange-600" data-id="element-1299">
          Overview
        </button>{' '}
        &gt;{' '}
        <button onClick={() => onNavigate?.('checkout')} className="hover:underline text-orange-600" data-id="element-1300">
          &lt;&lt;checkout&gt;&gt;
        </button>{' '}
        &gt; Integration Steps
      </div>

      <p className="text-gray-700 mb-8" data-id="element-1301">
        Integrating the &lt;&lt;checkout&gt;&gt; solution involves the following
        steps. Follow these steps to securely collect payments from your
        customers using the hosted payment page.
      </p>

      <StepList steps={steps} data-id="element-1302" />
    </DocPageLayout>;
}